import ISegment from "../../../types/services/ISegment";
import IThankYouAdminEmail from "../../../types/services/IThankYouAdminEmail";
import { isProductionDeployment } from "../../../utils/isProductionDeployment";

const INTERNAL_THANK_YOU_ADMIN_VISIBLE_CHARITY_IDS = new Set(["hts", "abc"]);

export interface StaffUserOption {
  id: string;
  label: string;
  email: string;
}

export function isOrdinaryDonorList(
  segment: Partial<ISegment> | null | undefined
): boolean {
  return Boolean(
    segment && segment.isDonorList && !segment.isKeyword && !segment.isAll
  );
}

export function staffDisplayName(user: {
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}): string {
  const combined = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  if (combined) {
    return combined;
  }
  if (user.name && user.name.trim()) {
    return user.name.trim();
  }
  return user.email || "";
}

function isInternalOperatorEmail(email: string): boolean {
  const at = email.lastIndexOf("@");
  if (at < 0) {
    return false;
  }
  return email
    .slice(at + 1)
    .toLowerCase()
    .split(".")
    .some(label => label === "viewspark" || label === "mahisoft");
}

function hideInternalThankYouAdminEmails(charityId?: string | null): boolean {
  if (!charityId || !isProductionDeployment()) {
    return false;
  }
  return !INTERNAL_THANK_YOU_ADMIN_VISIBLE_CHARITY_IDS.has(charityId);
}

function assignmentEmail(
  row: IThankYouAdminEmail & { userId?: string },
  users?: { [userId: string]: { email?: string } } | null
): string {
  if (row.email && row.email.trim()) {
    return row.email;
  }
  const user = row.userId && users ? users[row.userId] : null;
  return user && user.email ? user.email : "";
}

function isAssignableStaff(
  user: {
    disabled?: boolean;
    email?: string;
    role?: string;
  },
  charityId?: string | null
): boolean {
  const email = user && user.email ? user.email.trim() : "";
  if (
    !user ||
    user.disabled ||
    !email ||
    (user.role !== "ADMIN" && user.role !== "PUBLISHER")
  ) {
    return false;
  }
  return !(
    hideInternalThankYouAdminEmails(charityId) && isInternalOperatorEmail(email)
  );
}

export function assignableStaffOptions(
  users: {
    [userId: string]: {
      name?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      disabled?: boolean;
      role?: string;
    };
  } | null,
  charityId?: string | null
): StaffUserOption[] {
  if (!users) {
    return [];
  }
  return Object.keys(users)
    .filter(id => isAssignableStaff(users[id] || {}, charityId))
    .map(id => {
      const user = users[id] || {};
      const email = user.email || "";
      return {
        id,
        label: staffDisplayName(user) || email,
        email,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function parseWholeDollar(raw: string): number | null {
  const trimmed = (raw || "").trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  return Number(trimmed);
}

export function formatDollarAmount(
  value: number | string | null | undefined
): string {
  if (value == null || value === "") {
    return "None";
  }
  return `$${value}`;
}

export function assignmentRowsFromMap(
  rows: { [userId: string]: IThankYouAdminEmail } | null | undefined,
  options?: {
    charityId?: string | null;
    users?: { [userId: string]: { email?: string } } | null;
  }
): Array<IThankYouAdminEmail & { userId: string }> {
  if (!rows) {
    return [];
  }
  const mapped = Object.keys(rows)
    .map(userId => {
      const row = rows[userId] || {};
      return {
        ...row,
        userId,
      };
    })
    .sort((a, b) =>
      (a.name || a.email || a.userId).localeCompare(
        b.name || b.email || b.userId
      )
    );
  if (!hideInternalThankYouAdminEmails(options && options.charityId)) {
    return mapped;
  }
  return mapped.filter(
    row =>
      !isInternalOperatorEmail(assignmentEmail(row, options && options.users))
  );
}

export function assignedStaffKeys(
  rows: { [userId: string]: IThankYouAdminEmail } | null | undefined
): Set<string> {
  const keys = new Set<string>();
  if (!rows) {
    return keys;
  }
  Object.keys(rows).forEach(mapKey => {
    keys.add(mapKey);
    const row = rows[mapKey];
    if (row && row.userId) {
      keys.add(row.userId);
    }
    if (row && row.email) {
      keys.add(row.email.trim().toLowerCase());
    }
  });
  return keys;
}

export function isStaffAlreadyAssigned(
  option: StaffUserOption,
  assigned: Set<string>
): boolean {
  if (assigned.has(option.id)) {
    return true;
  }
  const email = (option.email || "").trim().toLowerCase();
  return Boolean(email && assigned.has(email));
}

export function staffAvailableToAssign(
  users: {
    [userId: string]: {
      name?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      disabled?: boolean;
      role?: string;
    };
  } | null,
  rows: { [userId: string]: IThankYouAdminEmail } | null | undefined,
  charityId?: string | null
): StaffUserOption[] {
  const assigned = assignedStaffKeys(rows);
  return assignableStaffOptions(users, charityId).filter(
    option => !isStaffAlreadyAssigned(option, assigned)
  );
}

export function listIdsForUser(
  assignments:
    | { [segmentId: string]: { [userId: string]: IThankYouAdminEmail } }
    | null
    | undefined,
  userId: string
): Array<{ segmentId: string; row: IThankYouAdminEmail }> {
  if (!assignments || !userId) {
    return [];
  }
  const matches: Array<{ segmentId: string; row: IThankYouAdminEmail }> = [];
  Object.keys(assignments).forEach(segmentId => {
    const rows = assignments[segmentId];
    if (rows && rows[userId]) {
      matches.push({ segmentId, row: rows[userId] });
    }
  });
  return matches;
}
